/** Inbox formatting shared by Notifications, Chat and Queries (prototype wording). */
import dayjs from 'dayjs';

import type { ChatMessage, ChatPartner } from '@shared/api/types';
import { FIRM_TIMEZONE } from '@shared/lib/date';

import { INBOX_TEXT } from '../constants';

/** `10-07-2026 14:05` — date and 24-hour time in the firm's timezone. */
export function stamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.tz(FIRM_TIMEZONE).format('MM-DD-YYYY HH:mm') : '';
}

/** Calendar-day label for chat separators: Today / Yesterday / MM-DD-YYYY. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = dayjs(iso).tz(FIRM_TIMEZONE).startOf('day');
  const today = dayjs(now).tz(FIRM_TIMEZONE).startOf('day');
  const diff = today.diff(d, 'day');
  if (diff === 0) return INBOX_TEXT.chat.today;
  if (diff === 1) return INBOX_TEXT.chat.yesterday;
  return d.format('MM-DD-YYYY');
}

/** `512 B`, `12 KB`, `3.4 MB`. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type Tick = 'sent' | 'delivered' | 'read';

export function tickOf(m: Pick<ChatMessage, 'deliveredAt' | 'readAt'>): Tick {
  if (m.readAt) return 'read';
  if (m.deliveredAt) return 'delivered';
  return 'sent';
}

export const TICK_TITLE: Record<Tick, string> = { sent: 'Sent', delivered: 'Delivered', read: 'Read' };

/** The list's preview line: "No messages yet" / "You: …" / "Sent an attachment". */
export function chatPreview(p: Pick<ChatPartner, 'lastMessage'>): string {
  const m = p.lastMessage;
  if (!m) return INBOX_TEXT.chat.noMessagesYet;
  const body = m.text || (m.hasFiles ? INBOX_TEXT.chat.sentAttachment : '');
  return m.mine ? `${INBOX_TEXT.chat.youPrefix}${body}` : body;
}

export function presenceLine(p: Pick<ChatPartner, 'online' | 'lastSeenAt'>): string {
  if (p.online) return INBOX_TEXT.chat.online;
  return p.lastSeenAt ? INBOX_TEXT.chat.lastSeen(stamp(p.lastSeenAt)) : INBOX_TEXT.chat.offline;
}

/** Files the chat composer and query replies accept (plan §8.12). */
export const ATTACH_ACCEPT = '.xlsx,.xls,.csv,.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp';

export type InboxTab = 'notifications' | 'chat' | 'queries';

export const INBOX_TABS: { key: InboxTab; label: string }[] = [
  { key: 'notifications', label: INBOX_TEXT.tabs.notifications },
  { key: 'chat', label: INBOX_TEXT.tabs.chat },
  { key: 'queries', label: INBOX_TEXT.tabs.queries },
];
